import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { HiOutlineLightBulb, HiOutlineArrowsExpand, HiOutlineDeviceTablet, HiArrowRight, HiOutlineSun, HiOutlineRefresh } from 'react-icons/hi';
import { MdOutlineLens, MdOutlineAccessibilityNew, MdAdsClick, MdOutlineRemoveRedEye, MdOutlineBrightnessHigh } from 'react-icons/md';

const VisionTest = () => {
  const [step, setStep] = useState(1);
  const [testResult, setTestResult] = useState('');

  const instructions = [
    { icon: <HiOutlineLightBulb />, text: 'Well-Lit Room', desc: 'Sit in a well-lit room for accuracy' },
    { icon: <MdOutlineBrightnessHigh />, text: 'Screen Brightness', desc: 'Adjust screen brightness to a comfortable level' },
    { icon: <HiOutlineSun />, text: 'Avoid Glare', desc: 'Ensure no reflections or glare on the screen' },
    { icon: <HiOutlineDeviceTablet />, text: 'Steady Device', desc: 'Keep device steady; do not move closer during the test' },
    { icon: <HiOutlineArrowsExpand />, text: '6 Feet Distance', desc: 'Stand approximately 6 feet (2 meters) away' },
    { icon: <MdOutlineRemoveRedEye />, text: 'Eye Level', desc: 'Ensure the screen is at eye level' },
    { icon: <MdOutlineLens />, text: 'Right Eye Test', desc: 'Cover left eye (no pressing), use right eye' },
    { icon: <HiOutlineRefresh />, text: 'Switch Eyes', desc: 'After completing, switch and test left eye' },
    { icon: <MdOutlineLens />, text: 'No Squinting', desc: 'Do not squint or strain your eyes' },
    { icon: <MdOutlineAccessibilityNew />, text: 'Corrective Lenses', desc: 'Test with glasses (or without if optional)' },
  ];

  const results = [
    { id: 'all', label: 'Perfect Clarity', desc: 'I can read all lines clearly' },
    { id: 'middle', label: 'Partial Clarity', desc: 'I can read up to the middle lines' },
    { id: 'top', label: 'Limited Clarity', desc: 'I can only read the top lines' },
    { id: 'none', label: 'Low Clarity', desc: 'I cannot read clearly' },
  ];

  const snellenLines = [
    { text: 'E', size: '80px' },
    { text: 'F P', size: '40px' },
    { text: 'T O Z', size: '28px' },
    { text: 'L P E D', size: '22px' },
    { text: 'P E C F D', size: '18px' },
    { text: 'E D F C Z P', size: '14px' },
    { text: 'E F L O P T Z', size: '11px' },
  ];

  return (
    <div className="pt-40 pb-24 px-4 min-h-screen bg-black mesh-gradient">
      <div className="max-w-4xl mx-auto relative z-10">
        {/* Luxury Header */}
        <div className="text-center mb-16 space-y-4">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/5 border border-white/10 rounded-lg">
            <span className="w-2 h-2 rounded-full bg-[var(--gold-primary)] animate-pulse" />
            <span className="text-[10px] font-black uppercase tracking-widest text-[var(--gold-primary)]">Ocular Diagnostic Unit</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-black text-white tracking-tight">
            Online <span className="text-gradient">Vision Test</span>
          </h1>
          <div className="max-w-2xl mx-auto p-4 bg-white/5 border border-white/10 rounded-xl">
            <p className="text-[10px] text-[var(--gold-primary)] font-black uppercase tracking-widest mb-1">Medical Disclaimer</p>
            <p className="text-xs text-slate-400 font-medium leading-relaxed italic">
              This test is for basic screening only and does not replace a professional eye examination. 
              For an accurate diagnosis, please consult a qualified eye care professional.
            </p>
          </div>
          <p className="text-slate-500 text-sm font-bold uppercase tracking-widest mt-6">
            Please follow these instructions carefully to get a more accurate result:
          </p>
        </div>

        <AnimatePresence mode="wait">
          {step === 1 && (
            <motion.div
              key="inst" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95 }}
              className="gold-card p-8 md:p-12"
            >
              <h3 className="text-xl font-black text-white mb-10 flex items-center gap-3">
                <span className="w-8 h-8 rounded-lg bg-[var(--gold-primary)] text-black flex items-center justify-center text-xs">01</span>
                Pre-Test Calibration
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-12">
                {instructions.map((item, idx) => (
                  <div key={idx} className="flex items-start gap-4 p-4 bg-white/5 rounded-xl border border-white/10 group hover:border-[var(--gold-primary)]/30 transition-colors">
                    <div className="w-8 h-8 rounded-lg bg-black border border-white/10 flex items-center justify-center text-[var(--gold-primary)] shadow-sm shrink-0">
                      {item.icon}
                    </div>
                    <div>
                      <span className="block text-xs font-black text-white leading-tight mb-1">{item.text}</span>
                      <span className="block text-[10px] font-medium text-slate-500 leading-tight">{item.desc}</span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex justify-center">
                <button
                  onClick={() => setStep(2)}
                  className="btn-primary"
                >
                  INITIALIZE TEST <HiArrowRight size={20} />
                </button>
              </div>
            </motion.div>
          )}

          {step === 2 && (
            <motion.div
              key="test" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, y: -20 }}
              className="space-y-12"
            >
              <div className="bg-white p-12 rounded-2xl shadow-[0_0_50px_rgba(255,255,255,0.1)] flex flex-col items-center gap-5 font-mono select-none overflow-x-auto">
                {snellenLines.map((line, idx) => (
                  <div key={idx} className="font-black tracking-[0.2em] text-black leading-none whitespace-nowrap" style={{ fontSize: line.size }}>
                    {line.text}
                  </div>
                ))}
              </div>

              <div className="gold-card p-8">
                <h4 className="text-lg font-black text-white mb-8 text-center uppercase tracking-widest">Select Visual Clarity</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                  {results.map((res) => (
                    <button
                      key={res.id}
                      onClick={() => setTestResult(res.id)}
                      className={`p-6 rounded-xl border-2 transition-all text-center ${
                        testResult === res.id 
                          ? 'border-[var(--gold-primary)] bg-[var(--gold-primary)]/10 shadow-[0_0_20px_var(--gold-glow)]' 
                          : 'border-white/5 bg-white/5 hover:border-white/10'
                      }`}
                    >
                      <p className={`font-black text-sm mb-1 ${testResult === res.id ? 'text-[var(--gold-primary)]' : 'text-white'}`}>{res.label}</p>
                      <p className="text-[10px] uppercase font-bold text-slate-500 tracking-tighter">{res.desc}</p>
                    </button>
                  ))}
                </div>

                <div className="flex gap-4 mt-12 pt-8 border-t border-white/10">
                  <button onClick={() => setStep(1)} className="flex-1 py-4 bg-white/5 text-slate-400 rounded-xl font-bold hover:bg-white/10 transition-colors">BACK</button>
                  <button
                    disabled={!testResult}
                    onClick={() => setStep(3)}
                    className={`flex-[2] py-4 rounded-xl font-black text-lg transition-all ${
                      testResult ? 'bg-[var(--gold-primary)] text-black hover:bg-[var(--gold-light)] active:scale-95' : 'bg-white/5 text-slate-600'
                    }`}
                  >
                    GENERATE REPORT
                  </button>
                </div>
              </div>
            </motion.div>
          )}

          {step === 3 && (
            <motion.div
              key="res" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
              className="gold-card p-10 md:p-20 text-center relative overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-[var(--gold-primary)]/10 blur-[50px] -mr-16 -mt-16" />
              <div className="w-20 h-20 bg-[var(--gold-primary)] text-black rounded-3xl flex items-center justify-center mx-auto mb-10 shadow-[0_0_30px_var(--gold-glow)]">
                <MdOutlineAccessibilityNew size={40} />
              </div>
              <h2 className="text-3xl font-black text-white mb-8">Vision Analysis <span className="text-gradient">Complete</span></h2>
              
              <div className="max-w-md mx-auto space-y-6 mb-12">
                <div className="p-8 bg-black border border-white/5 rounded-2xl">
                  <span className="text-[10px] font-black uppercase tracking-widest text-slate-500 block mb-2">Diagnostic Finding</span>
                  <span className="text-2xl font-black text-[var(--gold-primary)]">{results.find(r => r.id === testResult)?.label}</span>
                </div>
                
                <div className="p-8 bg-white/5 border border-white/10 rounded-2xl text-left">
                  <h4 className="flex items-center gap-2 text-[10px] font-black text-[var(--gold-primary)] uppercase tracking-[0.2em] mb-4">
                    <MdAdsClick size={16} /> Clinical Guidance
                  </h4>
                  <p className="text-sm font-medium text-slate-300 leading-relaxed italic">
                    {testResult === 'all' && "Your vision appears optimal. Continue regular breaks if working on digital screens."}
                    {testResult === 'middle' && "Standard visual level. We suggest a routine check-up within the next 12 months."}
                    {testResult === 'top' && "Vision is below standard. Please schedule a formal eye exam for corrective lenses."}
                    {testResult === 'none' && "Warning: Significantly reduced acuity. Visit an eye clinic immediately."}
                  </p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button onClick={() => { setStep(1); setTestResult(''); }} className="px-10 py-5 bg-white/5 text-slate-400 rounded-2xl font-black hover:bg-white/10 uppercase tracking-widest text-xs transition-colors">RETAKE TEST</button>
                <a href="/find-help" className="px-10 py-5 bg-[var(--gold-primary)] text-black rounded-2xl font-black hover:bg-[var(--gold-light)] transition-all active:scale-95 flex items-center justify-center gap-3 text-xs uppercase tracking-widest">
                  LOCATE SERVICES <HiArrowRight size={18} />
                </a>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default VisionTest;
